import React from 'react';
import { useEffect, useState, useMemo } from 'react';
import {
  AuditActivityListRequest,
  useFetchAuditActivityQuery,
} from '../auditactivity-api';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '@ant-design/pro-layout';
import {
  Alert,
  Breadcrumb,
  Card,
  Select,
  Space,
  Table,
  Row,
  Col,
  Tooltip,
  DatePicker,
  message,
  Modal,
} from 'antd';
import { HomeOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { AuditActivityViewModel, PaginatedResult, AuditActivityPaginatedResult } from '../../api/models';
import { DefaultPage, DefaultPageSize } from '../../../core/defaults';
import Title from 'antd/lib/typography/Title';
import { getLink } from '../../../App-router';
import { TableState } from '../../../components/table/model';
import { RootState } from '../../../store';
import { useSelector } from 'react-redux';
import { ConvertUTCtoCDT } from '../../../core/utils';
import dayjs, { Dayjs } from 'dayjs';

const { RangePicker } = DatePicker;

// Default range for the page: the last 7 days, ending now. Used both for the initial
// load and when the picker is cleared, so the two cannot drift apart.
const DEFAULT_RANGE_DAYS = 7;
const defaultFromUtc = () => dayjs().subtract(DEFAULT_RANGE_DAYS, 'day').startOf('day');
const defaultToUtc = () => dayjs();

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const CATEGORY_OPTIONS = [
  { label: 'Created', value: 'Created' },
  { label: 'Updated', value: 'Updated' },
   { label: 'Deleted', value: 'Deleted' },
  { label: 'Data Quality', value: 'DataQuality' },
];

export default function AuditActivityScreen(props: any) {
  const location = useLocation();
  const query = useQuery();
  const params: any = useParams<any>();
  const navigate = useNavigate();
  const token = useSelector((state: RootState) => state.auth.token);
const [jsonModalVisible, setJsonModalVisible] = useState(false);
const [selectedJson, setSelectedJson] = useState<any>(null);
  const getCurrentState = (): AuditActivityListRequest => {
  const fromUtc = query.get('fromUtc');
  const toUtc = query.get('toUtc');
  const filterParam = (name: string) => {
    const value = query.get(name);
    return value && value.trim() && value !== 'null' ? value : undefined;
  };
  return {
    pageNumber: Number(query.get('p')) || DefaultPage,
    pageSize: Number(query.get('size')) || DefaultPageSize,
    category: query.get('category') || 'Created',
    fromUtc: fromUtc || defaultFromUtc().toISOString(),
    toUtc: toUtc || defaultToUtc().toISOString(),
    customerName: filterParam('customerName'),
    partnerName: filterParam('partnerName'),
    partnerId: filterParam('partnerId'),
    source: filterParam('source'),
    target: filterParam('target'),
    transactionTypes: filterParam('transactionTypes'),
    entityName: filterParam('entityName'),
  };
};

// An error body is a ProblemDetails object, not a string. Rendering it directly throws
// "Objects are not valid as a React child" and takes the whole page down, so it is
// reduced to text here, surfacing the validation messages when there are any.
const describeApiError = (error: any): string => {
  const fallback = 'Failed to load audit activity';
  if (!error) return fallback;
  if (typeof error === 'string') return error;

  const body = error.data ?? error;
  if (typeof body === 'string' && body.trim()) return body;
  if (!body || typeof body !== 'object') return fallback;

  const details: string[] = [];
  const errors = body.errors;
  if (errors && typeof errors === 'object') {
    Object.keys(errors).forEach((field) => {
      const messages = errors[field];
      const text = Array.isArray(messages) ? messages.join(' ') : `${messages}`;
      details.push(field ? `${field}: ${text}` : text);
    });
  }

  const title = body.title ?? body.detail ?? body.message;
  const head = typeof title === 'string' && title.trim() ? title : fallback;
  return details.length ? `${head} (${details.join('; ')})` : head;
};

// Filter keys available per category, and where each reads its options from in a row.
// Mirrors CATEGORY_FIELDS below so the two stay easy to compare.
const FILTER_DEFS: Record<string, { label: string; field: string }> = {
  customerName: { label: 'Customer Name', field: 'customer' },
  partnerName: { label: 'Partner Name', field: 'partnerName' },
  partnerId: { label: 'Partner Id', field: 'partnerId' },
  source: { label: 'Source', field: 'source' },
  target: { label: 'Target', field: 'target' },
  transactionTypes: { label: 'Transaction Types', field: 'transactionTypes' },
};

const CATEGORY_FILTERS: Record<string, string[]> = {
  Created: ['customerName', 'partnerName', 'source', 'target', 'transactionTypes'],
  Updated: ['customerName', 'partnerName', 'source', 'target', 'transactionTypes', 'entityName'],
  Deleted: ['customerName', 'partnerName', 'source', 'target', 'transactionTypes', 'entityName'],
  DataQuality: ['partnerName', 'partnerId', 'customerName', 'source', 'target', 'entityName'],
};

// The single entityName filter is labelled and sourced differently per category.
const ENTITY_FILTER: Record<string, { label: string; field: string }> = {
  Updated: { label: 'Entity Changed', field: 'entityChanged' },
  Deleted: { label: 'Entity Deleted', field: 'entityChanged' },
  DataQuality: { label: 'Entity Name', field: 'tableName' },
};


  const currentRequest = useMemo(
    () => getCurrentState(),
    [location.search, params.tenantId]
  );

  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs(currentRequest.fromUtc),
    dayjs(currentRequest.toUtc),
  ]);

  useEffect(() => {
    setDateRange([dayjs(currentRequest.fromUtc), dayjs(currentRequest.toUtc)]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRequest.fromUtc, currentRequest.toUtc]);

  const {
    data: pagedData = {},
    isFetching,
    error,
  }: {
    data?: AuditActivityPaginatedResult | undefined;
    isFetching?: boolean | undefined;
    error?: any;
  } = useFetchAuditActivityQuery(currentRequest, {
    refetchOnMountOrArgChange: true,
    skip: false,
  });

  const navigateWithParams = (overrides: Record<string, any>) => {
    const queryParams: any = {
      p: DefaultPage,
      size: currentRequest.pageSize,
      category: currentRequest.category,
      fromUtc: currentRequest.fromUtc,
      toUtc: currentRequest.toUtc,
      customerName: currentRequest.customerName,
      partnerName: currentRequest.partnerName,
      partnerId: currentRequest.partnerId,
      source: currentRequest.source,
      target: currentRequest.target,
      transactionTypes: currentRequest.transactionTypes,
      entityName: currentRequest.entityName,
      ...overrides,
    };
    Object.keys(queryParams).forEach((key) => {
      const value = queryParams[key];
      if (value === undefined || value === null || `${value}`.trim() === '') {
        delete queryParams[key];
      }
    });
    navigate(getLink('AuditActivityList', { ...params }, queryParams));
  };

  // Switching category clears the filters, since the available criteria change with it
  // and a value carried over from another category would silently return nothing.
  const handleCategoryChange = (value: string) => {
    navigateWithParams({
      category: value,
      p: DefaultPage,
      customerName: undefined,
      partnerName: undefined,
      partnerId: undefined,
      source: undefined,
      target: undefined,
      transactionTypes: undefined,
      entityName: undefined,
    });
  };

  const handleFilterChange = (key: string, value?: string) => {
    navigateWithParams({ [key]: value, p: DefaultPage });
  };

  // Options come from the API, which returns the distinct values for the whole date
  // range rather than just the rows on this page, and computes them before the filters
  // are applied so choosing one filter never empties the others. Falls back to the
  // loaded rows if an older API is answering.
  const getUniqueValues = (field: string): string[] => {
    const fromApi = (pagedData as any)?.filterOptions?.[field];
    if (Array.isArray(fromApi) && fromApi.length) {
      return fromApi.map((v: any) => `${v}`);
    }
    const rows: any[] = (pagedData as any)?.results || [];
    const values = rows
      .map((row) => row?.[field])
      .filter((v) => v !== undefined && v !== null && `${v}`.trim() !== '')
      .map((v) => `${v}`);
    return Array.from(new Set(values)).sort((a, b) => a.localeCompare(b));
  };

  const activeFilters = CATEGORY_FILTERS[currentRequest.category] || CATEGORY_FILTERS.Created;

 const handleDateRangeChange = (values: any) => {
  if (!values || values.length !== 2 || !values[0] || !values[1]) {
    // User cleared the picker — reset to a sensible default range and apply immediately
      const defaultFrom = defaultFromUtc();
    const defaultTo = defaultToUtc();
    setDateRange([defaultFrom, defaultTo]);
    navigateWithParams({
      fromUtc: defaultFrom.toISOString(),
      toUtc: defaultTo.toISOString(),
      p: DefaultPage,
    });
    return;
  }
  setDateRange([values[0], values[1]]);
  navigateWithParams({
    fromUtc: values[0].toISOString(),
    toUtc: values[1].toISOString(),
    p: DefaultPage,
  });
};

 
  const handlePageChange = (page: number, pageSize?: number) => {
    navigateWithParams({ p: page, size: pageSize || currentRequest.pageSize });
  };

  const handlePageSizeChange = (current: number, size: number) => {
    navigateWithParams({ p: DefaultPage, size });
  };

  // Maps each Data Quality source table to its corresponding route path segment.
  const DATA_QUALITY_ROUTE_MAP: Record<string, string> = {
    PartnerConnectionEndpoint: 'partnerconnectionendpoints',
    CodeDecodeType: 'codedecodetypes',
    CodeDecodeValues: 'codedecodevalues',
    Customer: 'customers',
    PartnerInformation: 'partnershipInformations',
    PartnerContactInformation: 'partnercontactinformations',
     PartnerEDIX12: 'partneredix12s'
  };
  // Column definitions keyed by field name, reused across category-specific column sets
  const columnDefs: Record<string, any> = {
    timeStampUTC: {
      title: 'Timestamp',
      dataIndex: '',
      key: 'timeStampUTC',
      render: (item: AuditActivityViewModel) => (
        <span>{ConvertUTCtoCDT(item.timestampUtc || '')}</span>
      ),
    },
    userName: {
      title: 'User',
      dataIndex: '',
      key: 'userName',
      render: (item: AuditActivityViewModel) => <span>{item.userName || ''}</span>,
    },
    customer: {
      title: 'Customer Name',
      dataIndex: '',
      key: 'customer',
      render: (item: AuditActivityViewModel) => <span>{item.customer || ''}</span>,
    },
    partnerName: {
      title: 'Partner Name',
      dataIndex: '',
      key: 'partnerName',
      render: (item: AuditActivityViewModel) => <span>{item.partnerName || ''}</span>,
    },
    partnerId: {
      title: 'Partner Id',
      dataIndex: '',
      key: 'partnerId',
      render: (item: AuditActivityViewModel) => <span>{item.partnerId || ''}</span>,
    },
    source: {
      title: 'Source',
      dataIndex: '',
      key: 'source',
      render: (item: AuditActivityViewModel) => <span>{item.source || ''}</span>,
    },
    target: {
      title: 'Target',
      dataIndex: '',
      key: 'target',
      render: (item: AuditActivityViewModel) => <span>{item.target || ''}</span>,
    },
    transactionTypes: {
      title: 'Transaction Types',
      dataIndex: '',
      key: 'transactionTypes',
      render: (item: AuditActivityViewModel) => (
        <Tooltip title={item.transactionTypes || ''}>
          <span>{item.transactionTypes || ''}</span>
        </Tooltip>
      ),
    },
    entityChanged: {
      title: 'Entity Changed',
      dataIndex: '',
      key: 'entityChanged',
      render: (item: AuditActivityViewModel) => <span>{item.entityChanged || ''}</span>,
    },
    entityDeleted: {
      title: 'Entity Deleted',
      dataIndex: '',
      key: 'entityDeleted',
      render: (item: AuditActivityViewModel) => (
        <span>{item.entityChanged || ''}</span>
      ),
    },
    contentJson: {
      title: 'Content',
      dataIndex: '',
      key: 'contentJson',
      width: 120,
      render: (item: AuditActivityViewModel) => {
        const jsonContent = item.contentJson?.trim();

        if (!jsonContent) {
          return <span>-</span>;
        }

        return (
          <a
            onClick={() => {
              let formattedJson: any;

              try {
                formattedJson = JSON.parse(jsonContent);
              } catch {
                // If content is not valid JSON, show the raw value
                formattedJson = jsonContent;
              }

              setSelectedJson(formattedJson);
              setJsonModalVisible(true);
            }}
          >
            View JSON
          </a>
        );
      },
    },
  tableName: {
  title: 'Entity Name',
  dataIndex: '',
  key: 'tableName',
  render: (item: AuditActivityViewModel) => {
    if (item.tableName === 'CodeDecodeValues') {
      return (
        <Link
          to={`/cob/codedecodevalues/${item.codeDecodeTypeId}/${item.recordId}`}
        >
          {item.tableName}
        </Link>
      );
    }
     if (item.tableName === 'SplittingRule') {
      if (item.partnerId && item.recordId) {
        return (
          <Link to={`/cob/carrieronboardings/${item.partnerId}/${item.recordId}`}>
            {item.tableName}
          </Link>
        );
      }
      return <span>{item.tableName}</span>;
    }
    const routeSegment = item.tableName
      ? DATA_QUALITY_ROUTE_MAP[item.tableName]
      : undefined;

    if (routeSegment && item.recordId) {
      return (
        <Link to={`/cob/${routeSegment}/${item.recordId}`}>
          {item.tableName}
        </Link>
      );
    }

    return <span>{item.tableName || ''}</span>;
  },
},
    columnName: {
      title: 'Column Name',
      dataIndex: '',
      key: 'columnName',
      render: (item: AuditActivityViewModel) => <span>{item.columnName || ''}</span>,
    },
    recordId: {
      title: 'Record Id',
      dataIndex: '',
      key: 'recordId',
      render: (item: AuditActivityViewModel) => <span>{item.recordId || ''}</span>,
    },
    value: {
      title: 'Value',
      dataIndex: '',
      key: 'value',
      render: (item: AuditActivityViewModel) => (
        <Tooltip title={item.value || ''}>
          <span>{item.value || ''}</span>
        </Tooltip>
      ),
    },
    reason: {
      title: 'Reason',
      dataIndex: '',
      key: 'reason',
      render: (item: AuditActivityViewModel) => (
        <Tooltip title={item.reason || ''}>
          <span>{item.reason || ''}</span>
        </Tooltip>
      ),
    },
  };

  // Which fields to show, per category
  const CATEGORY_FIELDS: Record<string, string[]> = {
    Created: ['timeStampUTC', 'userName', 'customer', 'partnerName', 'source', 'target', 'transactionTypes'],
    Updated: [
      'timeStampUTC',
      'userName',
      'customer',
      'partnerName',
      'source',
      'target',
      'transactionTypes',
      'entityChanged',
    ],
    Deleted: [
    'timeStampUTC',
    'userName',
    'customer',
    'partnerName',
    'source',
    'target',
    'transactionTypes',
    'entityDeleted',
     'contentJson',
  ],
   DataQuality: [
      'timeStampUTC',
      'userName',
      'partnerName',
      'partnerId',
      'customer',
      'source',
      'target',
      'tableName',
      'columnName',
      'recordId',
      'value',
      'reason',
    ],
  };

  const activeFields = CATEGORY_FIELDS[currentRequest.category] || CATEGORY_FIELDS.Created;

  const auditActivityTable: TableState = {
    columns: activeFields.map((field) => columnDefs[field]),
  };

  return (
    <div>
      <PageHeader
        title={
          <Title level={4} style={{ marginBottom: 0, display: 'inline-block' }}>
            Audit Activity
          </Title>
        }
        breadcrumb={
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to={getLink('home', { tenantId: params.tenantId })}>
                <HomeOutlined />
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to={getLink('cob', { tenantId: params.tenantId })}>Dashboard</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item key="Audit Activity">
              <Link to={getLink('AuditActivityList', { tenantId: params.tenantId })}>
                Audit Activity
              </Link>
            </Breadcrumb.Item>
          </Breadcrumb>
        }
      >
        <Card>
          {/* Same gutter and column spans as the filter row below, so the labels in
              each column line up across both rows. The date range spans the two
              remaining columns because the picker needs the width. */}
          <Row gutter={[16, 16]}>
            <Col className="gutter-row" span={8}>
              <Space size="large" style={{ width: '100%' }}>
                <div style={{ minWidth: 150, flexShrink: 0 }}>Category</div>
                <Select
                  style={{ minWidth: 200, maxWidth: '100%', width: '100%' }}
                  value={currentRequest.category}
                  onChange={handleCategoryChange}
                  options={CATEGORY_OPTIONS}
                />
              </Space>
            </Col>
            <Col className="gutter-row" span={16}>
              <Space size="large" style={{ width: '100%' }}>
                <div style={{ minWidth: 150, flexShrink: 0 }}>From Time - To Time</div>
                <RangePicker
                  style={{ minWidth: 300, maxWidth: '100%', width: '100%' }}
                  showTime
                  value={dateRange}
                  onChange={handleDateRangeChange}
                  allowClear
                />
              </Space>
            </Col>
          </Row>
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            {activeFilters.map((key) => {
              const def =
                key === 'entityName'
                  ? ENTITY_FILTER[currentRequest.category] || { label: 'Entity Name', field: 'tableName' }
                  : FILTER_DEFS[key];
              if (!def) return null;
              const value = (currentRequest as any)[key] as string | undefined;
              return (
                <Col className="gutter-row" span={8} key={key}>
                  <Space size="large" style={{ width: '100%' }}>
                    <div style={{ minWidth: 150, flexShrink: 0 }}>{def.label}</div>
                    <Select
                      style={{ minWidth: 200, maxWidth: '100%', width: '100%' }}
                      showSearch
                      allowClear
                      placeholder="Search..."
                      optionFilterProp="children"
                      loading={isFetching}
                      value={value || undefined}
                      onChange={(next?: string) => handleFilterChange(key, next)}
                      options={getUniqueValues(def.field).map((item) => ({ label: item, value: item }))}
                    />
                  </Space>
                </Col>
              );
            })}
          </Row>
        </Card>

        <Card>
          {error?.status && (
            <Alert message="Error" description={describeApiError(error)} type="error" showIcon />
          )}

         {currentRequest.category === 'DataQuality' && (
  <Alert
    showIcon
    icon={<InfoCircleOutlined style={{ color: '#595959' }} />}
    style={{
      marginBottom: 16,
      backgroundColor: '#F5F5F5',
      border: '1px solid #D9D9D9',
    }}
    message={
      <span style={{ color: '#595959', fontWeight: 600 }}>
        What's checked under Data Quality
      </span>
    }
    description={
  <div style={{ color: '#595959' }}>
    <div><b>Whitespace Validation</b></div>
    <div>
      Customer Qualifier, Partner Name, Contact Name, Contact Email,
      Contact Phone, Code Decode Type, and Code Decode Value.
    </div>

    <br />

    <div><b>Folder Path Validation</b></div>
    <div>
      Detects spaces, leading '/', double '//', and environment-specific
      Test paths in Partner Connection Endpoint folder paths.
    </div>

    <br />

    <div><b>EDI Validation</b></div>
    <div>
      Checks PartnerEDIX12 InterchangeTestIndicator values by environment.
    </div>

    <br />

    <div><b>Routing Rule Validation</b></div>
    <div>
      Flags outbound routing rules (204, 300, 211) that do not have a
      corresponding PartnerEDIX12 entry.
    </div>

    <br />

    <div><b>PartnerEDIX12 Rule Validation</b></div>
    <div>
      Flags PartnerEDIX12 entries that do not have a corresponding
      Connection End Point entry.
    </div>

    <br />

    <div><b>Splitting Rule Validation</b></div>
    <div>
      Checks SplittingRule TestFlag values by environment.
    </div>
  </div>
}
  />
)}

          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Table
              scroll={{ x: true }}
              dataSource={pagedData?.results || []}
              rowKey={(record, index) =>
                `${record.tableName}-${record.columnName}-${record.recordId}-${record.timestampUtc}-${index}`
              }
              pagination={{
                current: currentRequest.pageNumber,
                pageSize: currentRequest.pageSize,
                total: pagedData?.totalCount || 0,
                showTotal: (total) => `Total ${total} items`,
                pageSizeOptions: ['10', '20', '50', '100', '10000'],
                showSizeChanger: true,
                onChange: handlePageChange,
                onShowSizeChange: handlePageSizeChange,
                locale: { items_per_page: ' / Page' },
              }}
              columns={auditActivityTable.columns}
              loading={isFetching}
            />
            <Modal
            title="Deleted Record Details"
            open={jsonModalVisible}
            onCancel={() => setJsonModalVisible(false)}
            footer={null}
            width={800}
          >
            <pre>
              {typeof selectedJson === 'string'
                ? selectedJson
                : JSON.stringify(selectedJson, null, 2)}
            </pre>
          </Modal>
          </Space>
        </Card>
      </PageHeader>
    </div>
  );
}